import { CS_LABELS, DA_LABELS, D_LABELS, HS_LABELS, REPORT_GROUPS, SA_LABELS } from './reportCodes';
import { lookupProse, makeCautionKey, makeGroupKey } from './reportTemplates';

/**
 * Pure helpers for editing a pre-generated cloud_eval_reports/{evaluationId}
 * document (see the v1 report contract). Nothing here scores an eval: codes
 * come from the generator or from the clinician, and prose comes from the
 * published template by key lookup.
 */

export const SECTION_ORDER = ['caution', 'FA', 'MA', 'FAD', 'MAD', 'FC', 'MC'];

export const SECTION_HEADINGS = {
  caution: 'Caution',
  FA: 'Female Adults',
  MA: 'Male Adults',
  FAD: 'Female Adolescents',
  MAD: 'Male Adolescents',
  FC: 'Female Children',
  MC: 'Male Children',
};

export const RISK_OPTIONS = ['low', 'moderate', 'moderate-high', 'high'];

/** V3 option labels per stored code name (Eric's key). */
export const CODE_LABELS = {
  cs: CS_LABELS,
  sa: SA_LABELS,
  da: DA_LABELS,
  hs: HS_LABELS,
  d: D_LABELS,
};

const GROUP_CODE_NAMES = ['sa', 'da', 'hs'];

export function groupFor(sectionId) {
  return REPORT_GROUPS.find((group) => group.id === sectionId) ?? null;
}

/** Code names (lower case, as stored) a section needs before it can be keyed. */
export function sectionCodeNames(sectionId) {
  if (sectionId === 'caution') return ['cs'];
  const group = groupFor(sectionId);
  if (!group) return [];
  return group.hasD ? [...GROUP_CODE_NAMES, 'd'] : GROUP_CODE_NAMES;
}

export function sectionCodes(codes, sectionId) {
  if (sectionId === 'caution') return codes?.caution ?? {};
  return codes?.groups?.[sectionId] ?? {};
}

function isMissing(value) {
  return value === null || value === undefined;
}

export function sectionNeeds(codes, sectionId) {
  const scored = sectionCodes(codes, sectionId);
  return sectionCodeNames(sectionId)
    .filter((name) => isMissing(scored[name]))
    .map((name) => name.toUpperCase());
}

export function sectionKey(codes, sectionId) {
  const scored = sectionCodes(codes, sectionId);
  if (sectionId === 'caution') return makeCautionKey(scored.cs);
  const group = groupFor(sectionId);
  if (!group) return null;
  if (group.hasD && isMissing(scored.d)) return null;
  return makeGroupKey({
    group: group.id,
    sa: scored.sa,
    da: scored.da,
    hs: scored.hs,
    d: group.hasD ? scored.d : null,
  });
}

/**
 * Put the honorific into template prose. "Mr. []" / "Ms. []" become the
 * honorific itself so it never reads "Mr. Mr. Baugher". With no honorific
 * the [] slots are left visible for the clinician to notice.
 */
export function fillHonorific(prose, honorific) {
  if (!prose) return '';
  const name = String(honorific ?? '').trim();
  if (!name) return String(prose);
  return String(prose)
    .replace(/\b(?:Mr|Ms|Mrs|Miss|Mx|Dr)\.?\s*\[\]/g, name)
    .replace(/\[\]/g, name);
}

/** Template prose for a key, honorific already filled; '' when missing. */
export function templateText(templateKeys, key, honorific) {
  if (!key) return '';
  return fillHonorific(lookupProse(templateKeys, key), honorific);
}

/**
 * Sections in contract order. Any section the generator did not write is
 * created empty so the editor always shows all seven blocks.
 */
export function normalizeSections(report) {
  const byId = new Map((report?.sections ?? []).map((section) => [section.id, section]));
  return SECTION_ORDER.map((id) => {
    const existing = byId.get(id);
    return {
      id,
      heading: existing?.heading || SECTION_HEADINGS[id],
      key: existing?.key ?? sectionKey(report?.codes, id),
      text: existing?.text ?? '',
      state: existing?.state ?? 'needs_codes',
      needs: existing?.needs ?? sectionNeeds(report?.codes, id),
    };
  });
}

/**
 * The editable copy of a report. Field names match the Firestore document
 * so save can address them with field paths.
 */
export function toEditableReport(report) {
  return {
    header: {
      clientName: report?.header?.clientName ?? '',
      honorific: report?.header?.honorific ?? '',
      age: report?.header?.age ?? null,
      assessmentDate: report?.header?.assessmentDate ?? '',
      provider: report?.header?.provider ?? '',
    },
    intro: report?.intro ?? '',
    codes: {
      caution: {
        cs: report?.codes?.caution?.cs ?? null,
        source: report?.codes?.caution?.source ?? null,
      },
      groups: Object.fromEntries(REPORT_GROUPS.map((group) => {
        const scored = report?.codes?.groups?.[group.id] ?? {};
        return [group.id, {
          sa: scored.sa ?? null,
          da: scored.da ?? null,
          hs: scored.hs ?? null,
          d: group.hasD ? (scored.d ?? null) : null,
          source: { ...(scored.source ?? {}) },
          evidence: scored.evidence ?? { cells: [] },
        }];
      })),
    },
    sections: normalizeSections(report),
    summary: { text: report?.summary?.text ?? '', summaryId: report?.summary?.summaryId ?? null },
    risk: report?.risk ?? null,
    closing: report?.closing ?? '',
  };
}

/**
 * Re-key one section after its codes changed. A section the clinician has
 * hand-edited keeps its text; otherwise its text is swapped for the
 * published template paragraph (or emptied when there is none).
 */
export function rekeySection(section, codes, templateKeys, honorific) {
  const key = sectionKey(codes, section.id);
  const needs = sectionNeeds(codes, section.id);
  // Hand-edited text is never replaced; neither is text when the template
  // could not be loaded (null), so a failed load cannot blank a section.
  if (section.state === 'edited' || templateKeys == null) {
    return { ...section, key, needs };
  }
  if (!key) {
    return { ...section, key, needs, text: '', state: 'needs_codes' };
  }
  const text = templateText(templateKeys, key, honorific);
  return { ...section, key, needs, text, state: text ? 'filled' : 'missing_template' };
}

/** Set one code (cs / sa / da / hs / d) and re-key that section. */
export function changeCode(report, sectionId, codeName, value, templateKeys) {
  const nextValue = isMissing(value) ? null : Number(value);
  const codes = sectionId === 'caution'
    ? {
      ...report.codes,
      caution: { ...report.codes.caution, cs: nextValue, source: 'edited' },
    }
    : {
      ...report.codes,
      groups: {
        ...report.codes.groups,
        [sectionId]: {
          ...report.codes.groups[sectionId],
          [codeName]: nextValue,
          source: { ...report.codes.groups[sectionId].source, [codeName]: 'edited' },
        },
      },
    };
  return {
    ...report,
    codes,
    sections: report.sections.map((section) => (
      section.id === sectionId
        ? rekeySection(section, codes, templateKeys, report.header.honorific)
        : section
    )),
  };
}

export function changeSectionText(report, sectionId, text) {
  return {
    ...report,
    sections: report.sections.map((section) => (
      section.id === sectionId ? { ...section, text, state: 'edited' } : section
    )),
  };
}

/** Drop a hand edit and take the published template paragraph again. */
export function revertSectionToTemplate(report, sectionId, templateKeys) {
  return {
    ...report,
    sections: report.sections.map((section) => (
      section.id === sectionId
        ? rekeySection({ ...section, state: 'filled' }, report.codes, templateKeys, report.header.honorific)
        : section
    )),
  };
}

function sameValue(left, right) {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

function changedFields(before, after, names) {
  const changed = {};
  names.forEach((name) => {
    if (!sameValue(before?.[name], after?.[name])) changed[name] = after[name] ?? null;
  });
  return Object.keys(changed).length ? changed : null;
}

/**
 * PATCH /admin/cloud-reports/{id} body: only what changed between the
 * loaded report and the edited copy. Sections are keyed by id and carry
 * only their changed text / key / state. The backend marks edited code
 * sources and the edit metadata itself.
 */
export function buildReportPatch(original, edited) {
  const patch = {};
  const header = changedFields(original.header, edited.header, Object.keys(edited.header));
  if (header) patch.header = header;
  if (!sameValue(original.intro, edited.intro)) patch.intro = edited.intro;

  const codes = {};
  const caution = changedFields(original.codes.caution, edited.codes.caution, ['cs']);
  if (caution) codes.caution = caution;
  const groups = {};
  REPORT_GROUPS.forEach((group) => {
    const changed = changedFields(
      original.codes.groups[group.id],
      edited.codes.groups[group.id],
      sectionCodeNames(group.id)
    );
    if (changed) groups[group.id] = changed;
  });
  if (Object.keys(groups).length) codes.groups = groups;
  if (Object.keys(codes).length) patch.codes = codes;

  const sections = {};
  const before = new Map(original.sections.map((section) => [section.id, section]));
  edited.sections.forEach((section) => {
    const changed = changedFields(before.get(section.id), section, ['text', 'key', 'state']);
    if (changed) sections[section.id] = changed;
  });
  if (Object.keys(sections).length) patch.sections = sections;

  const summary = changedFields(original.summary, edited.summary, ['text']);
  if (summary) patch.summary = summary;
  if (!sameValue(original.risk, edited.risk)) patch.risk = edited.risk ?? null;
  if (!sameValue(original.closing, edited.closing)) patch.closing = edited.closing;
  return patch;
}
